import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { ibeKpiSelectionsRead } from 'actions/kpi/beKpiSelection.action';
import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import KPI_SCOPE from 'constants/staff/kpiScope.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useAccessIds } from 'providers/Access.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import byId from 'utils/predicates/byId.util';

import EditKpisInstructionsModal from './EditKpisInstructionsModal.component';
import ScoreCardEdit from './ScoreCardEdit.component';

import CN from './EditKpisPage.module.scss';

const L = logger('EditKpisPage'); // eslint-disable-line no-unused-vars

const Spin = ({ text }) => (
  <div className={CN.center}>
    <InlineSpinner variant="big" text={text} />
  </div>
);

const EditKpisContents = ({ scope, abeStylists, ibeKpiSelectionsRead }) => {
  const { accessStylistId } = useAccessIds();

  const [operation, setOperation] = useStateWhileMounted();

  const {
    value: valueStylists,
    loading: isLoadingStylists,
    error: errorStylists,
  } = useAsync(async () => {
    const result = await abeStylists();
    return filterActiveStylists(result?.is?.ok ? result?.json : []);
  }, [abeStylists]);

  const [{ value: valueSelections, loading: isLoadingSelections, error: errorSelections }, readSelections] =
    useAsyncFn(async () => {
      const result = await ibeKpiSelectionsRead({ scope });
      return result?.is?.ok ? result?.json : [];
    }, [scope, ibeKpiSelectionsRead]);

  const handleCardChange = useCallback(
    (ev) => {
      setOperation(ev?.type);

      const promise = readSelections();
      promise.finally(() => void setOperation(null));

      return promise;
    },
    [readSelections, setOperation],
  );

  useEffect(() => void readSelections(), [readSelections]);

  const isSubtleOp = 'move' === operation || 'add' === operation || 'delete' === operation;

  const isLoading = isLoadingStylists || isLoadingSelections;
  const isNotReady = isLoading || !valueStylists || !valueSelections;
  const isError = fromAnyToBool(errorSelections || errorStylists);

  const isBusinessInScope = KPI_SCOPE.business === scope || KPI_SCOPE.stylists === scope;
  const isBusinessFull = isFullArray(Object.keys(valueSelections?.business || {}));

  const isMessageLoading = isLoading && !isSubtleOp;
  const isMessageError = isError && !isMessageLoading;
  const isMessageNotReady = isNotReady && !isSubtleOp && !isMessageLoading && !isMessageError;
  const isDataRendered = isSubtleOp || (!isLoading && !isError && valueStylists && valueSelections);

  const ownSelections = valueSelections?.stylists?.[accessStylistId];
  const ownUserName = asArray(valueStylists).find(byId(accessStylistId))?.userName;
  const isPersonalScope = KPI_SCOPE.personal === scope;

  if (isError) L.warn('EditKpisContents()', { errorSelections, errorStylists });

  return (
    <div data-bem="EditKpisContents" className={CN.outer}>
      <div className={CN.cardsContainer}>
        {isMessageLoading && <Spin text="Hold tight - we’re loading your KPI data!" />}
        {isMessageError && <Spin text="Error loading your KPI data!" />}
        {isMessageNotReady && <Spin text="No data!" />}

        {isDataRendered && isBusinessFull && isBusinessInScope && (
          <ScoreCardEdit scope={scope} value={valueSelections?.business} onChange={handleCardChange} />
        )}

        {isDataRendered && isPersonalScope && (
          <ScoreCardEdit
            scope={scope}
            stylistName={ownUserName}
            value={ownSelections}
            onChange={handleCardChange}
            isEmptyPersonal
          />
        )}

        {isDataRendered &&
          !isPersonalScope &&
          asArray(valueStylists).map((stylist) => {
            const { id, userName } = stylist ?? {};
            const stylistSelections = valueSelections?.stylists?.[id];

            return (
              <ScoreCardEdit
                key={id}
                scope={scope}
                stylistName={userName}
                value={stylistSelections}
                onChange={handleCardChange}
              />
            );
          })}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => bindActionCreators({ abeStylists, ibeKpiSelectionsRead }, dispatch);

const EditKpisContentsHoc = connect(null, mapDispatchToProps)(EditKpisContents);

const EditKpisPage = () => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const summaryKpiLink = linkTo({ pattern: LINK.slnStaffReportsKpi, params: { salonId }, fault: '#' });

  const floatingToolbarRef = useRef(null);

  return (
    <div
      data-bem="EditKpisPage"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.floatingToolbar} ref={floatingToolbarRef}>
        <div data-bem="EditKpisPage__panel" className={CN.panel}>
          <div className={CN.title}>
            <div className={CN.iconContainer}>
              <Icon variant="reports" width="xl" />
            </div>
            <ResponsiveBox.Desktop>Customise KPIs</ResponsiveBox.Desktop>
            <ResponsiveBox.Mobile>Edit KPIs</ResponsiveBox.Mobile>
          </div>

          <div className={CN.space}>
            <EditKpisInstructionsModal tag="EditKpisPage__EditKpisInstructionsModal" />
            <NavLink to={summaryKpiLink}>
              <Button variant="primaryForce" onClick={alwaysNoop}>
                <span className={CN.navLinkColor}>VIEW KPI REPORT</span>
              </Button>
            </NavLink>
          </div>
        </div>

        <Permitted not value={PERM.canEditKpisPersonal}>
          <Permitted not value={PERM.canEditKpisAllStaff}>
            <AlertPanel tag="AlertPanel" variant="warningPanel">
              <div className={CN.alertText}>
                Sorry, you do not have permission to access this feature.
                <br />
                If you need access, please speak to your manager about editing your permissions settings.
              </div>
            </AlertPanel>
          </Permitted>
        </Permitted>
      </div>

      <Permitted value={PERM.canEditKpisPersonal}>
        <EditKpisContentsHoc scope="personal" />
      </Permitted>

      <Permitted value={PERM.canEditKpisAllStaff}>
        <EditKpisContentsHoc scope="stylists" />
      </Permitted>
    </div>
  );
};

export default EditKpisPage;
